/**
 * Derived targets, creative wiring, and save handler for the sent scheduled multi-country modal.
 */

import { useCallback, useMemo, useState } from 'react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { buildBulkEditDropLabelMapsFromOrdered } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/shared/bulkEditDropLabels';
import { buildGlobalCountrySequence } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/shared/perDropGeneration';
import { useMultiCountryCreative } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/hooks/useMultiCountryCreative';
import type { useMultiCountryBulkEditForm } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/hooks/useMultiCountryBulkEditForm';
import type { EditSentDropPayload, SentEditSaveMeta } from '@/features/smswiser-campaigns/components/modals/sent/EditSentDropModal.types';

import {
  buildSentScheduledWizardSave,
  countAffectedScheduledSentEdits,
  isScheduledSentPrepared,
  mergeBulkEditScheduledSentIds,
} from '../shared';
import { useMultiCountrySentScheduledEditForm } from './useMultiCountrySentScheduledEditForm';

interface CatalogForCountry {
  gateways: Array<{ id: number; name: string; pricePerSms?: number }>;
  campaigns: Array<{ id: number; name: string }>;
  tlds: Array<{ value: string; displayName: string }>;
  leadsLists?: Array<{ id: number; name: string }>;
}

interface Params {
  selectedIds: string[];
  mergedDrops: PreparedDrop[];
  catalogByCountry: Record<string, CatalogForCountry>;
  onClose: () => void;
  onSave: (payloads: EditSentDropPayload[], meta?: SentEditSaveMeta) => void | Promise<void>;
}

export function useMultiCountrySentScheduledEditModalState(params: Params) {
  const { selectedIds, mergedDrops, catalogByCountry, onClose, onSave } = params;
  const [isBusy, setIsBusy] = useState(false);

  const { countryKeys, dropCountByCountry, sourceCountryKey } = useMemo(() => {
    const byId = new Map(mergedDrops.map((d) => [d.id, d]));
    const countMap = new Map<string, number>();
    const keys = new Set<string>();
    for (const id of selectedIds) {
      const d = byId.get(id);
      if (!d) continue;
      keys.add(d.countryKey);
      countMap.set(d.countryKey, (countMap.get(d.countryKey) ?? 0) + 1);
    }
    const sorted = [...keys].sort();
    return { countryKeys: sorted, dropCountByCountry: countMap, sourceCountryKey: sorted[0] ?? '' };
  }, [selectedIds, mergedDrops]);

  const form = useMultiCountrySentScheduledEditForm({ selectedCountryKeys: countryKeys });

  const affectedScheduledCount = useMemo(
    () => countAffectedScheduledSentEdits(selectedIds, form.propagateToCountries, mergedDrops),
    [selectedIds, mergedDrops, form.propagateToCountries],
  );

  const allDropCountByCountry = useMemo(() => {
    const map = new Map(dropCountByCountry);
    for (const ck of form.propagateToCountries) {
      const n = mergedDrops.filter((d) => isScheduledSentPrepared(d) && d.countryKey === ck).length;
      if (n > 0) map.set(ck, Math.max(map.get(ck) ?? 0, n));
    }
    return map;
  }, [dropCountByCountry, form.propagateToCountries, mergedDrops]);

  const bulkEditTargets = useMemo(() => {
    const orderedIds = mergeBulkEditScheduledSentIds(selectedIds, form.propagateToCountries, mergedDrops);
    const leadsListsByCountry = Object.fromEntries(
      Object.entries(catalogByCountry).map(([ck, c]) => [ck, c.leadsLists ?? []]),
    );
    return {
      orderedIds,
      globalCountryPerDrop: buildGlobalCountrySequence(orderedIds, mergedDrops),
      ...buildBulkEditDropLabelMapsFromOrdered(orderedIds, mergedDrops, leadsListsByCountry),
    };
  }, [selectedIds, mergedDrops, form.propagateToCountries, catalogByCountry]);

  const creative = useMultiCountryCreative({
    countryKeys: form.allCountryKeys,
    dropCountByCountry: allDropCountByCountry,
    globalCountryPerDrop: bulkEditTargets.globalCountryPerDrop,
    onTextsGenerated: form.saveGeneratedTextsByCountry,
    onSendersGenerated: form.saveGeneratedSendersByCountry,
    onTextsPerDropMergeOrder: form.saveGeneratedTextsPerDropMergeOrder,
    onSendersPerDropMergeOrder: form.saveGeneratedSendersPerDropMergeOrder,
    syncPerDropSmsTexts: form.saveGeneratedTextsPerDropMergeOrder,
    syncSmsTextsByCountry: form.saveGeneratedTextsByCountry,
  });

  const { flat: allDropLabels, byCountry: dropLabelsByCountry } = bulkEditTargets;
  const creativeForm = form as unknown as ReturnType<typeof useMultiCountryBulkEditForm>;

  const handleSave = useCallback(async () => {
    setIsBusy(true);
    try {
      const { payloads, syncPreparedStaticTags } = buildSentScheduledWizardSave(
        selectedIds,
        mergedDrops,
        form,
      );
      const hasSmswiser = payloads.length > 0;
      const hasStatic = (syncPreparedStaticTags?.length ?? 0) > 0;
      if (!hasSmswiser && !hasStatic) return;
      const meta: SentEditSaveMeta | undefined = hasStatic ? { syncPreparedStaticTags } : undefined;
      await onSave(payloads, meta);
      onClose();
    } finally {
      setIsBusy(false);
    }
  }, [form, mergedDrops, onClose, onSave, selectedIds]);

  return {
    form,
    creative,
    sourceCountryKey,
    affectedScheduledCount,
    allDropLabels,
    dropLabelsByCountry,
    creativeForm,
    handleSave,
    isBusy,
  };
}
